import { useState, useMemo } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { ConvexError } from "convex/values";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../../convex/_generated/api";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import { reportWriteFailure } from "@/lib/reportWriteFailure";
import type { PrayerTheme } from "@/lib/store";

/**
 * /sisters/prayer-wall
 *
 * PERSISTENCE: this screen reads and writes Convex (convex/community.ts —
 * feedPosts filtered to kind "prayer", feedComments for replies). Until this
 * change it read client/src/lib/store.ts, an in-memory object: a prayer
 * posted here lived in one browser tab and was gone on refresh, mixed in with
 * seeded posts attributed to "Hannah Doyle" that read as real requests from
 * real women. A prayer wall where nobody sees anybody is worse than not
 * having one.
 *
 * ANONYMITY: "Show my name" is enforced by the SERVER, not this component.
 * `api.community.prayerFeed` never sends an anonymous sister's authorId,
 * name, initials, coach badge or tier to anyone but her — see
 * `authorViewFor` in convex/community.ts. This file only renders whatever
 * the query already decided is safe to show.
 *
 * The Prayer Wall is the many-to-many counterpart to Sister Pairs. Sisters
 * post prayer requests, optionally anonymously and tagged by season. Other
 * sisters tap "Praying" (no likes, just prayer counts) and can leave a short
 * note or verse reference.
 *
 * Empty-state, theme filter, anonymous toggle, and a soft compose at the top.
 */

const THEMES: Array<{ key: PrayerTheme | "all"; label: string }> = [
  { key: "all",         label: "All sisters" },
  { key: "postpartum",  label: "Postpartum" },
  { key: "motherhood",  label: "Motherhood" },
  { key: "marriage",    label: "Marriage" },
  { key: "body_image",  label: "Body image" },
  { key: "grief",       label: "Grief" },
  { key: "faith",       label: "Faith" },
  { key: "work",        label: "Work" },
  { key: "general",     label: "General" },
];

/** The `code` a Convex handler threw, when it threw a structured ConvexError. */
function errorCode(err: unknown): string | null {
  if (err instanceof ConvexError) {
    const data = err.data as { code?: unknown } | undefined;
    if (typeof data?.code === "string") return data.code;
  }
  return null;
}

type FeedRow = FunctionReturnType<typeof api.community.prayerFeed>[number];

export default function PrayerWall() {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const [filter, setFilter] = useState<PrayerTheme | "all">("all");
  const themeArg = filter === "all" ? undefined : filter;

  // Convex throws NOT_AUTHORIZED on prayerFeed without a session, and a
  // thrown query surfaces as a render error. Skipping is what keeps a
  // signed-out render from taking the page down.
  const feedArgs = isAuthenticated ? (themeArg ? { theme: themeArg } : {}) : ("skip" as const);
  const feed = useQuery(api.community.prayerFeed, feedArgs);

  if (!isAuthenticated) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          {authLoading ? "Finding your place at the wall..." : "Sign in to see the Prayer Wall, sister."}
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow="Sisterhood"
        title={
          <>
            Prayer <span className="italic">wall</span>
          </>
        }
        subtitle={
          <>
            "Carry each other's burdens, and in this way you will fulfill the law of Christ."
            <span style={{ color: "rgba(229,185,151,0.95)" }}> · Galatians 6:2</span>
          </>
        }
        trailing={
          <Link href="/sisters">
            <span
              className="inline-flex items-center justify-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: "rgba(245,240,232,0.14)",
                color: "#F5F0E8",
                cursor: "pointer",
              }}
              aria-label="Back to sisters"
            >
              <Icon name="arrow_back" size={18} />
            </span>
          </Link>
        }
      />

      <div className="px-5 -mt-3 pb-6 space-y-4">
        <ComposePrayer />
        <ThemeFilter value={filter} onChange={setFilter} />

        {feed === undefined ? (
          <GlassCard organic padding={22}>
            <p
              className="mnf-serif italic"
              style={{ fontSize: 15, color: "var(--mnf-tg)", lineHeight: 1.5, textAlign: "center" }}
            >
              Gathering the wall...
            </p>
          </GlassCard>
        ) : feed.length === 0 ? (
          <GlassCard organic padding={22}>
            <p
              className="mnf-serif italic"
              style={{ fontSize: 15, color: "var(--mnf-tg)", lineHeight: 1.5, textAlign: "center" }}
            >
              No prayers in this season yet. Be the first to lift one up, sister.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            {feed.map((p) => (
              <PrayerRow key={p.id} prayer={p} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* --------------------------------- Compose -------------------------------- */

function ComposePrayer() {
  const createFeedPost = useMutation(api.community.createFeedPost);
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [theme, setTheme] = useState<PrayerTheme | "">("");
  const [posted, setPosted] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit() {
    const text = body.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await createFeedPost({
        kind: "prayer",
        body: text,
        anonymous,
        ...(theme ? { theme } : {}),
      });
      // Only clear the box once the server has it. On failure her words stay
      // where she typed them.
      setBody("");
      setAnonymous(false);
      setTheme("");
      setPosted(true);
      window.setTimeout(() => setPosted(false), 2400);
    } catch (err) {
      reportWriteFailure(
        "community",
        "posting your prayer",
        err,
        "Your prayer didn't send",
        errorCode(err) === "NOT_AUTHORIZED"
          ? "We couldn't confirm you're signed in. Your words are still here — reload and try again."
          : "Nothing was saved. Your words are still in the box — check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <GlassCard organic padding={18}>
      <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
        <span style={{ fontSize: 16 }} aria-hidden>🙏</span>
        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-f)", letterSpacing: "0.16em", fontSize: 10 }}
        >
          LIFT YOUR REQUEST
        </p>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder="What is on your heart, sister? She is listening."
        className="w-full mnf-body"
        style={{
          background: "rgba(255,255,255,0.55)",
          border: "1px solid rgba(30,74,53,0.10)",
          borderRadius: 14,
          padding: 12,
          fontSize: 13,
          color: "var(--mnf-tx)",
          resize: "none",
          outline: "none",
          lineHeight: 1.5,
        }}
        data-testid="input-prayer-body"
      />

      <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 10 }}>
        <select
          value={theme}
          onChange={(e) => setTheme(e.target.value as PrayerTheme | "")}
          className="mnf-body"
          style={{
            padding: "7px 10px",
            borderRadius: 999,
            border: "1px solid rgba(30,74,53,0.16)",
            background: "rgba(255,255,255,0.7)",
            color: "var(--mnf-fd)",
            fontSize: 12,
            cursor: "pointer",
            outline: "none",
          }}
          data-testid="select-prayer-theme"
        >
          <option value="">Choose a season (optional)</option>
          <option value="postpartum">Postpartum</option>
          <option value="motherhood">Motherhood</option>
          <option value="marriage">Marriage</option>
          <option value="body_image">Body image</option>
          <option value="grief">Grief</option>
          <option value="faith">Faith</option>
          <option value="work">Work</option>
          <option value="general">General</option>
        </select>

        <button
          onClick={() => setAnonymous(!anonymous)}
          className="flex items-center gap-1.5"
          style={{
            padding: "7px 12px",
            borderRadius: 999,
            border: anonymous ? "1.5px solid var(--mnf-purple, #7F77DD)" : "1px solid rgba(30,74,53,0.16)",
            background: anonymous ? "rgba(127,119,221,0.12)" : "rgba(255,255,255,0.7)",
            color: anonymous ? "var(--mnf-purple, #7F77DD)" : "var(--mnf-tg)",
            fontSize: 12,
            cursor: "pointer",
          }}
          data-testid="button-prayer-anonymous"
        >
          <Icon name={anonymous ? "visibility_off" : "visibility"} size={14} />
          <span>{anonymous ? "Anonymous" : "Show my name"}</span>
        </button>

        <div style={{ marginLeft: "auto" }}>
          <Btn
            variant="primary"
            size="md"
            onClick={submit}
            disabled={!body.trim() || saving}
            style={{ opacity: body.trim() && !saving ? 1 : 0.55 }}
          >
            <Icon name="send" size={14} />
            <span style={{ marginLeft: 6 }}>Post</span>
          </Btn>
        </div>
      </div>

      {posted && (
        <p
          className="mnf-body italic"
          style={{ fontSize: 12, color: "var(--mnf-f)", marginTop: 10 }}
        >
          Your prayer is on the wall. The sisters are with you.
        </p>
      )}
    </GlassCard>
  );
}

/* ------------------------------ Theme filter ------------------------------ */

function ThemeFilter({
  value,
  onChange,
}: {
  value: PrayerTheme | "all";
  onChange: (v: PrayerTheme | "all") => void;
}) {
  return (
    <div
      className="flex items-center gap-2 overflow-x-auto"
      style={{ paddingBottom: 4, scrollbarWidth: "none" }}
    >
      {THEMES.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className="mnf-body whitespace-nowrap"
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: active ? "1.5px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.14)",
              background: active ? "rgba(30,74,53,0.10)" : "rgba(255,255,255,0.6)",
              color: active ? "var(--mnf-f)" : "var(--mnf-tg)",
              fontSize: 12,
              fontWeight: active ? 600 : 400,
              cursor: "pointer",
              flexShrink: 0,
            }}
            data-testid={`button-theme-${t.key}`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------- Prayer row ------------------------------- */

function PrayerRow({ prayer }: { prayer: FeedRow }) {
  const [showReply, setShowReply] = useState(false);
  const bloomPost = useMutation(api.community.bloomPost);
  const [lifting, setLifting] = useState(false);
  const replies = useQuery(api.community.feedComments, { postId: prayer.id });

  async function lift() {
    if (lifting) return;
    setLifting(true);
    try {
      await bloomPost({ postId: prayer.id });
    } catch (err) {
      reportWriteFailure(
        "community",
        "saving your prayer lift",
        err,
        "That didn't save",
        "We couldn't reach the wall. Nothing changed — try again in a moment.",
      );
    } finally {
      setLifting(false);
    }
  }

  const themeLabel = useMemo(
    () => THEMES.find((t) => t.key === prayer.theme)?.label,
    [prayer.theme],
  );

  return (
    <GlassCard organic padding={18}>
      <div className="flex items-start gap-3">
        <Avatar
          initials={prayer.authorInitials}
          size={42}
          ringColor={prayer.anonymous ? "rgba(127,119,221,0.45)" : undefined}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className="mnf-serif"
              style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}
            >
              {prayer.authorName}
            </p>
            {themeLabel && <PillTag tone="forest">{themeLabel}</PillTag>}
            {prayer.isOwn && <PillTag tone="coral">Your prayer</PillTag>}
            <span
              className="mnf-eyebrow"
              style={{
                fontSize: 9,
                color: "var(--mnf-tg)",
                letterSpacing: "0.14em",
                marginLeft: "auto",
              }}
            >
              {relativeTime(prayer.createdAt)}
            </span>
          </div>
          <p
            className="mnf-body"
            style={{
              fontSize: 13.5,
              color: "var(--mnf-fd)",
              lineHeight: 1.55,
              marginTop: 6,
              fontStyle: "italic",
            }}
          >
            "{prayer.body}"
          </p>

          {/* Action row */}
          <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
            <button
              onClick={lift}
              className="flex items-center gap-1.5 transition-all hover-elevate"
              style={{
                padding: "7px 12px",
                borderRadius: 999,
                border: prayer.lifted
                  ? "1.5px solid var(--mnf-coral, #C0784A)"
                  : "1px solid rgba(30,74,53,0.14)",
                background: prayer.lifted
                  ? "rgba(192,120,74,0.14)"
                  : "rgba(255,255,255,0.7)",
                color: prayer.lifted ? "var(--mnf-coral, #C0784A)" : "var(--mnf-fd)",
                fontSize: 11.5,
                fontWeight: 600,
                letterSpacing: "0.04em",
                cursor: "pointer",
                opacity: lifting ? 0.6 : 1,
              }}
              disabled={lifting}
              data-testid={`button-lift-${prayer.id}`}
            >
              <Icon name={prayer.lifted ? "favorite" : "favorite_border"} size={13} />
              <span>{prayer.lifted ? "Praying" : "Pray with her"}</span>
              <span style={{ opacity: 0.7 }}>· {prayer.liftCount}</span>
            </button>

            <button
              onClick={() => setShowReply((s) => !s)}
              className="flex items-center gap-1.5"
              style={{
                padding: "7px 12px",
                borderRadius: 999,
                border: "1px solid rgba(30,74,53,0.14)",
                background: "rgba(255,255,255,0.7)",
                color: "var(--mnf-tg)",
                fontSize: 11.5,
                cursor: "pointer",
              }}
              data-testid={`button-reply-${prayer.id}`}
            >
              <Icon name="menu_book" size={13} />
              <span>Verse</span>
              {prayer.replyCount > 0 && <span style={{ opacity: 0.7 }}>· {prayer.replyCount}</span>}
            </button>
          </div>

          {/* Replies */}
          {replies && replies.length > 0 && (
            <ul style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {replies.map((r) => (
                <li
                  key={r.id}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "rgba(127,119,221,0.06)",
                    border: "1px solid rgba(127,119,221,0.15)",
                  }}
                >
                  <p
                    className="mnf-body"
                    style={{ fontSize: 12, color: "var(--mnf-fd)", lineHeight: 1.45 }}
                  >
                    <span style={{ fontWeight: 600 }}>{r.authorName}</span>
                    {r.reference && (
                      <span style={{ color: "var(--mnf-purple, #7F77DD)" }}> · {r.reference}</span>
                    )}
                  </p>
                  <p
                    className="mnf-body italic"
                    style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 2 }}
                  >
                    "{r.body}"
                  </p>
                </li>
              ))}
            </ul>
          )}

          {showReply && (
            <ReplyComposer prayerId={prayer.id} onClose={() => setShowReply(false)} />
          )}
        </div>
      </div>
    </GlassCard>
  );
}

/* ----------------------------- Reply composer ----------------------------- */

function ReplyComposer({
  prayerId,
  onClose,
}: {
  prayerId: string;
  onClose: () => void;
}) {
  const addFeedComment = useMutation(api.community.addFeedComment);
  const [body, setBody] = useState("");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    const text = body.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await addFeedComment({
        postId: prayerId,
        body: text,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      });
      setBody("");
      setReference("");
      onClose();
    } catch (err) {
      reportWriteFailure(
        "community",
        "sending your reply",
        err,
        "Your reply didn't send",
        "Nothing was saved. Your words are still here — check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 10,
        padding: 10,
        borderRadius: 12,
        background: "rgba(127,119,221,0.06)",
        border: "1px solid rgba(127,119,221,0.18)",
      }}
    >
      <input
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        placeholder="Verse reference (optional, e.g. Psalm 27:14)"
        className="w-full mnf-body"
        style={{
          background: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(127,119,221,0.18)",
          borderRadius: 10,
          padding: "7px 10px",
          fontSize: 12,
          color: "var(--mnf-fd)",
          outline: "none",
          marginBottom: 6,
        }}
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        placeholder="A few words of encouragement, sister."
        className="w-full mnf-body"
        style={{
          background: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(127,119,221,0.18)",
          borderRadius: 10,
          padding: "8px 10px",
          fontSize: 12,
          color: "var(--mnf-fd)",
          resize: "none",
          outline: "none",
          lineHeight: 1.5,
        }}
      />
      <div className="flex items-center justify-end gap-2" style={{ marginTop: 6 }}>
        <button
          onClick={onClose}
          className="mnf-body"
          style={{
            fontSize: 11.5,
            color: "var(--mnf-tg)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "6px 10px",
          }}
        >
          Cancel
        </button>
        <Btn variant="primary" size="sm" onClick={submit} disabled={!body.trim() || saving}>
          Send
        </Btn>
      </div>
    </div>
  );
}

/* --------------------------------- Helpers -------------------------------- */

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  return `${d}d ago`;
}
